import {
    addCompanyMessages,
    addCompanyStaff,
    getCompanyChampionships,
    getCompanyFacilities,
    getCompanyGame, removeCompanyStaff
} from "../models/company.js";
import {getTagTeamDisplayData} from "./tagTeams.js";
import {
    addInterestedRelationship, getRosterMemberSuperstars,
    getSuperstarDisplayData, removeSuperstarFactionRelationships, removeSuperstarManagerRelationships,
    removeSuperstarsFromManager, removeSuperstarTagTeams, removeTraitFromSuperstar,
    updateSuperstarMorale,
    vacateChampionships
} from "./superstars.js";
import {getRandomChoice, getRandomInt} from "./helpers.js";
import {
    createPreparingFinishedMessage,
    createReassureFailureMessage,
    createReassureSuccessMessage,
    createRecruitmentFailMessage,
    createRecruitmentSuccessMessage,
    createScoutingReportMessage,
    createSuperstarInterestedScoutMessage,
    createSuperstarInterestMessage, createTrainingFinishedMessage,
    createTransitionFinishedMessage,
    createTransitionToStaffFinishedMessage,
    createTransitionToStaffFinishedSuperstarMessage,
    createTransitionToSuperstarFinishedMessage,
    createUnlearnTraitFailureMessage,
    createUnlearnTraitSuccessMessage
} from "./messages.js";
import {transferTalentToRoster} from "./rosters.js";
import {
    addSuperstarStaffObjects, getSuperstarFeuds,
    getSuperstarManager, getSuperstarNegotiations,
    getSuperstarRoster,
    getSuperstarStaffObject, getSuperstarTalentRequests
} from "../models/superstar.js";
import {getStaffSuperstarObject, Staff} from "../models/staff.js";
import {Negotiations, StaffMembers, TalentRequests} from "../models/models.js";
import {getCompanyRosterByType, getTransitionToStaffMemberRoleBoostData, removeStaffTaskData} from "./companies.js";
import {addRosterSuperstars, removeRosterSuperstars} from "../models/roster.js";
import {getFeudRating} from "./feuds.js";
import {unlockSpecialSegment, updateSuperstarWorkrate} from "./processMatch.js";
import {updateSuperstarCharisma} from "./segments.js";
import {checkTalentRequestAction} from "./talentRequests.js";
import {traitsData} from "../data/traits.js";
import {loadUser} from "./user.js";

async function scoutTalent(talent, indieRoster, indieAvailableRoster, scout, company) {  // scout_talent
    let game = await getCompanyGame(company);
    let talentType = talent.objectName;
    let talentInfo;
    if (talentType === 'tagTeam') {
        talentInfo = await getTagTeamDisplayData({tagTeam: talent});
    } else {
        talentInfo = await getSuperstarDisplayData({superstar: talent});
    }

    // Recruit talent chance
    let chances = {1: 20, 2: 40, 3: 60};
    let scoutLevel = scout ? scout.level : 1;
    let recruited = false;

    if (getRandomInt(0, 100) <= (chances[scoutLevel] || 60)) {
        await makeTalentAvailable(talent, indieAvailableRoster);
        recruited = true;
    }

    let newMessage = await createScoutingReportMessage(talentInfo, scout, game.monthCount, game.year, recruited);
    await addCompanyMessages(company, [newMessage]);

    return newMessage;
}

async function recruitTalent(talent, indieRoster, indieAvailableRoster, scout, company) {  // recruit_talent
    let game = await getCompanyGame(company);
    let talentType = talent.objectName;
    let scoutLevel = scout ? scout.level : 1;
    let recruitmentChances = {1: 40, 2: 60, 3: 80};
    let chance = recruitmentChances[scoutLevel] || 80;

    let newMessage;
    if (getRandomInt(0, 100) <= chance) {
        // Build interest chance
        let buildInterestChances = {2: 10, 3: 20};
        let interested = false;
        let scoutingFacility = (await getCompanyFacilities(company)).find(x => x.name === 'Scouting');

        if (scoutLevel >= 2 && talentType === 'superstar' && scoutingFacility && scoutingFacility.level >= 2) {
            if (getRandomInt(0, 100) <= (buildInterestChances[scoutLevel] || 20)) {
                await addInterestedRelationship(talent, company);
                interested = true;
            }
        }

        await makeTalentAvailable(talent, indieAvailableRoster);
        newMessage = await createRecruitmentSuccessMessage(talent, game.monthCount, game.year, scout, interested);
        await addCompanyMessages(company, [newMessage]);
    } else {
        newMessage = await createRecruitmentFailMessage(talent, game.monthCount, game.year, scout);
        await addCompanyMessages(company, [newMessage]);
    }

    return newMessage;
}

async function makeTalentAvailable(talent, indieAvailableRoster) {  // make_talent_available
    // Move to indie available roster
    let talentSuperstars = await getRosterMemberSuperstars(talent);
    let superstarToTransfer = talentSuperstars[0];
    let transferred = await transferTalentToRoster(superstarToTransfer, indieAvailableRoster);
    for (let superstar of talentSuperstars) {
        superstar.availableThisMonth = true;
    }
    return transferred;
}

async function buildInterest(indieAvailableSuperstars, scout, company) {  // build_interest
    let game = await getCompanyGame(company);
    let chances = {2: 10, 3: 20};
    let scoutLevel = scout ? scout.level : 1;

    let superstar = null;
    if (indieAvailableSuperstars.length && getRandomInt(0, 100) <= (chances[scoutLevel] || 20)) {
        superstar = getRandomChoice(indieAvailableSuperstars);
        await addInterestedRelationship(superstar, company);

        // Add message from superstar or scout
        let newMessage;
        if (getRandomInt(0, 100) <= 50) {
            newMessage = await createSuperstarInterestMessage(superstar, scout, game.monthCount, game.year);
        } else {
            newMessage = await createSuperstarInterestedScoutMessage(superstar, scout, game.monthCount, game.year);
        }
        await addCompanyMessages(company, [newMessage]);
    }
    return superstar;
}

async function reassureTalent(talent, talentManager, company) {  // reassure_talent
    let game = await getCompanyGame(company);
    let talentManagerLevel = talentManager ? talentManager.level : 1;
    let moraleChangeDict = {1: 8, 2: 13, 3: 20};
    let reassureChances = {1: 50, 2: 60, 3: 70};

    let chance = reassureChances[talentManagerLevel] || 70;
    if (getRandomInt(0, 100) <= chance) {
        let moraleChange = (moraleChangeDict[talentManagerLevel] || 20) + getRandomInt(0, 3);
        updateSuperstarMorale(talent, moraleChange);

        let newMessage = await createReassureSuccessMessage(talent, moraleChange, game.monthCount, game.year, talentManager);
        await addCompanyMessages(company, [newMessage]);
    } else {
        let moraleChange = getRandomInt(-6, -3);
        updateSuperstarMorale(talent, moraleChange);

        let newMessage = await createReassureFailureMessage(talent, moraleChange, game.monthCount, game.year, talentManager);
        await addCompanyMessages(company, [newMessage]);
    }

    return true;
}

async function transitionTalent(talent, trainer, company) {  // transition_talent
    let game = await getCompanyGame(company);

    let role;
    if (talent.fullTimeManager) {
        talent.fullTimeManager = false;
        role = 'superstar';
    } else {
        talent.fullTimeManager = true;
        role = 'manager';
        let manager = await getSuperstarManager(talent);
        if (manager) {
            await removeSuperstarsFromManager(manager, [talent]);
        }
    }

    let newMessage = await createTransitionFinishedMessage(talent, game.monthCount, game.year, trainer, role);
    await addCompanyMessages(company, [newMessage]);

    return true;
}

async function unlearnTalentTrait(talent, staffMember, company) {  // unlearn_talent_trait
    let game = await getCompanyGame(company);
    let staffMemberLevel = staffMember ? staffMember.level : 1;
    let unlearnChances = {1: 60, 2: 75, 3: 90};
    let chance = unlearnChances[staffMemberLevel] || 90;
    let traitToRemove = staffMember.taskData['trait_name'];

    if (getRandomInt(0, 100) <= chance && traitToRemove) {
        removeTraitFromSuperstar(talent, traitToRemove);
        let newMessage = await createUnlearnTraitSuccessMessage(talent, game.monthCount, game.year, traitToRemove,
            staffMember);
        await addCompanyMessages(company, [newMessage]);
    } else {
        let newMessage = await createUnlearnTraitFailureMessage(talent, game.monthCount, game.year, traitToRemove,
            staffMember);
        await addCompanyMessages(company, [newMessage]);
    }

    return true;
}

async function transitionSuperstarToStaffMember(superstar, taskStaffMember, company) {  // transition_superstar_to_staff_member
    let role = taskStaffMember.taskData['role_name'];
    let game = await getCompanyGame(company);
    let month = game.monthCount;
    let year = game.year;
    let superstarRoster = await getSuperstarRoster(superstar);

    // Check for existing staff object, otherwise create new
    let existingStaffObject = await getSuperstarStaffObject(superstar);
    let staffObject;
    if (existingStaffObject) {
        staffObject = existingStaffObject;
    } else {
        let newStaffObject = Staff();
        await StaffMembers.insert(newStaffObject);
        newStaffObject.role = role;
        await addSuperstarStaffObjects(superstar, [newStaffObject]);  // Link staff object to superstar
        staffObject = newStaffObject;
        // Add xp boost
        let roleBoostData = getTransitionToStaffMemberRoleBoostData(superstar, role);
        newStaffObject.xp = roleBoostData['xpBoost'];
    }

    // Update shared attributes
    staffObject.name = superstar.name;
    staffObject.gender = superstar.gender;
    staffObject.age = superstar.age;
    staffObject.graphic = superstar.graphic;

    // Add staff member to company
    await addCompanyStaff(company, [staffObject]);

    // Remove superstar from their roster
    if (superstarRoster) {
        await removeRosterSuperstars(superstarRoster, [superstar]);
    }

    // Remove championships, tag teams, relationships
    await vacateChampionships({superstar: superstar, championships: (await getCompanyChampionships(company)), month, year});
    await removeSuperstarTagTeams(superstar);
    await removeSuperstarFactionRelationships(superstar, month, year);
    await removeSuperstarManagerRelationships(superstar);
    superstar.brandName = null;
    for (const negotiation of (await getSuperstarNegotiations(superstar))) {
        await Negotiations.remove(negotiation);
    }
    for (const talentRequest of (await getSuperstarTalentRequests(superstar))) {
        await TalentRequests.remove(talentRequest)
    }
    for (const feud of (await getSuperstarFeuds(superstar))) {
        if (!feud.ended) {
            feud.ended = true;
            feud.endMonth = month;
            feud.endYear = year;
            if (!feud.started) {
                feud.rating = null;
            } else {
                feud.rating = await getFeudRating(feud);
            }
        }
    }

    // Reset temporary attributes
    superstar.faceOfTheCompanyCounter = 0;
    superstar.suspensionMonths = 0;
    superstar.kayfabePointsTemporary = 0;
    superstar.winsTemporary = 0;
    superstar.lossesTemporary = 0;

    // Add messages
    let newMessage = await createTransitionToStaffFinishedMessage(superstar, month, year, taskStaffMember, role);
    await addCompanyMessages(company, [newMessage]);
    let newSuperstarMessage = await createTransitionToStaffFinishedSuperstarMessage(superstar, month, year, role);
    await addCompanyMessages(company, [newSuperstarMessage]);

    return true;
}

async function transitionStaffMemberToSuperstar(staffMember, taskStaffMember, company) {  // transition_staff_member_to_superstar
    let game = await getCompanyGame(company);
    let month = game.monthCount;
    let year = game.year;

    // Add superstar to company's main roster
    let superstarObject = await getStaffSuperstarObject(staffMember);
    let mainRoster = await getCompanyRosterByType(company, 'main');
    await addRosterSuperstars(mainRoster, [superstarObject]);

    // Remove staff member from their company
    await removeCompanyStaff(company, [staffMember]);

    // Reset temporary attributes
    removeStaffTaskData(staffMember);
    staffMember.unavailableMonths = 0;

    // Update shared attributes
    superstarObject.name = staffMember.name;
    superstarObject.gender = staffMember.gender;
    superstarObject.age = staffMember.age;
    superstarObject.graphic = staffMember.graphic;

    // Add message
    let newMessage = await createTransitionToSuperstarFinishedMessage(staffMember, month, year, taskStaffMember);
    await addCompanyMessages(company, [newMessage]);

    return true;
}

async function trainTalent(talent, skill, trainer, company, staffMembers) {  // train_talent
    const game = await getCompanyGame(company);
    const user = loadUser();
    const xpGainedDict = {1: 25, 2: 35, 3: 45};

    if (!skill) {
        skill = getRandomChoice(['workrate', 'charisma'])
    }
    let xpGained = xpGainedDict[trainer.level] || 45;

    // Check for training xp multiplier trait
    for (let trait of talent.traits) {
        let traitData = traitsData[trait];
        if (traitData && traitData['training_xp_multiplier']) {
            xpGained *= traitData['training_xp_multiplier'];
        }
    }
    xpGained = Math.round(xpGained);

    // Add xp to skill
    if (skill === 'workrate') {
        talent.workrateXp += xpGained;
        let levelIncrease = await updateSuperstarWorkrate(talent, company, staffMembers, game.monthCount, game.year);

        // Unlock special segment
        if (levelIncrease && talent.workrate === 3) {
            await unlockSpecialSegment(company, 'Dummy Wrestling', game.monthCount, game.year);
        }
    } else if (skill === 'charisma') {
        talent.charismaXp += xpGained;
        await updateSuperstarCharisma(talent, company, staffMembers, game.monthCount, game.year);
    }

    let data = {
        'skill': skill,
        'xpGained': xpGained
    };

    // Add message from trainer
    let newMessage = await createTrainingFinishedMessage(talent, data, game.monthCount, game.year, trainer);
    await addCompanyMessages(company, [newMessage]);

    // Check talent request
    let requestData = {'skill': skill};
    await checkTalentRequestAction({company, requestType: 'train_skill_x', talent, data: requestData});

    return data;
}

async function makeTrainingTalentAvailable(talent, trainer, trainingAvailableRoster, company) {  // make_training_talent_available
    let game = await getCompanyGame(company);
    await transferTalentToRoster(talent, trainingAvailableRoster);

    // Add message from trainer
    let newMessage = await createPreparingFinishedMessage(talent, game.monthCount, game.year, trainer);
    await addCompanyMessages(company, [newMessage]);

    return true;
}

function getTalentRehabDoctor(talent, doctors) {  // get_talent_rehab_doctor
    return doctors.find(x => x.taskData && x.taskData['talent_id'] === talent.$loki);
}

function getRehabChance(doctor, medicalFacility) {  // get_rehab_chance
    if (doctor.level >= 3 && medicalFacility.level >= 3) {
        return 25;
    } else {
        return 15;
    }
}

export {scoutTalent, reassureTalent, recruitTalent, buildInterest, unlearnTalentTrait, transitionTalent, trainTalent,
    makeTrainingTalentAvailable, getTalentRehabDoctor, getRehabChance, transitionSuperstarToStaffMember,
    transitionStaffMemberToSuperstar}